import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Plus, ShieldCheck, ShieldAlert, Lock, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function ExtraLinesPanel({ jobId, draft, onChanged }) {
  const extraLines = draft.extra_lines || [];
  const locked = !!draft.already_exported;

  const [services, setServices] = useState([]);
  const [items, setItems] = useState([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({ service_id: '', qbo_item_id: '', unit_price: '', vat_treatment: '', description: '' });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/services`, { headers: authHeaders() }),
      axios.get(`${API}/api/sales-invoicing/reference-lists/item`, { headers: authHeaders() }),
    ])
      .then(([svcRes, itemRes]) => {
        setServices(svcRes.data || []);
        setItems((itemRes.data?.items || []).filter((i) => i.is_active));
        setListsLoaded(true);
      })
      .catch((err) => toast.error(getApiError(err, 'Could not load services/QBO items.')));
  }, []);

  const contractedServiceIds = useMemo(() => new Set((draft.service_lines || []).map((s) => s.service_id)), [draft.service_lines]);
  const usedExtraServiceIds = useMemo(() => new Set(extraLines.filter((l) => l.id !== editingId).map((l) => l.service_id)), [extraLines, editingId]);
  const availableServices = services.filter((s) => !contractedServiceIds.has(s.id) && !usedExtraServiceIds.has(s.id));

  const resetForm = () => {
    setForm({ service_id: '', qbo_item_id: '', unit_price: '', vat_treatment: '', description: '' });
    setAdding(false);
    setEditingId(null);
  };

  const startEdit = (line) => {
    setEditingId(line.id);
    setAdding(true);
    setForm({
      service_id: String(line.service_id), qbo_item_id: String(line.qbo_item_id),
      unit_price: String(line.unit_price), vat_treatment: line.vat_treatment, description: line.description || '',
    });
  };

  const submit = async () => {
    if (!form.service_id || !form.qbo_item_id || !form.vat_treatment) {
      toast.error('Pick a service, a QBO item and a VAT treatment \u2014 all three are required.');
      return;
    }
    const price = Number(form.unit_price);
    if (!(price > 0)) {
      toast.error('Enter a price greater than 0 for this invoice.');
      return;
    }
    setSaving(true);
    const body = {
      service_id: Number(form.service_id), qbo_item_id: Number(form.qbo_item_id),
      unit_price: price, vat_treatment: form.vat_treatment, description: form.description || null,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/api/sales-invoicing/jobs/${jobId}/extra-lines/${editingId}`, body, { headers: authHeaders() });
        toast.success('Extra line updated.');
      } else {
        await axios.post(`${API}/api/sales-invoicing/jobs/${jobId}/extra-lines`, body, { headers: authHeaders() });
        toast.success('Extra line added to this invoice only.');
      }
      resetForm();
      await onChanged();
    } catch (err) {
      toast.error(getApiError(err, 'Could not save this extra line.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lineId) => {
    try {
      await axios.delete(`${API}/api/sales-invoicing/jobs/${jobId}/extra-lines/${lineId}`, { headers: authHeaders() });
      toast.success('Extra line removed \u2014 it will not appear on any future invoice unless added again.');
      await onChanged();
    } catch (err) {
      toast.error(getApiError(err, 'Could not remove this extra line.'));
    }
  };

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3 space-y-3" data-testid="extra-lines-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-sky-800 uppercase">Extra lines — exceptional services for this invoice only</div>
        {locked && (
          <Badge variant="secondary" className="bg-slate-200 text-slate-700" data-testid="extra-lines-locked-badge">
            <Lock className="h-3 w-3 mr-1" />Locked — already exported
          </Badge>
        )}
      </div>

      {extraLines.length === 0 && !adding && (
        <div className="text-sm text-slate-500" data-testid="extra-lines-empty">No extra lines on this invoice.</div>
      )}

      <div className="space-y-2" data-testid="extra-lines-list">
        {extraLines.map((l) => (
          <div key={l.id} className="flex items-center justify-between gap-3 text-sm border rounded-md p-2 bg-white" data-testid={`extra-line-${l.id}`}>
            <div className="min-w-0">
              <div className="font-medium truncate">{l.service_name}</div>
              <div className="text-slate-400 text-xs truncate">{l.description || 'No description'} &middot; £{Number(l.unit_price).toFixed(2)} &middot; {l.vat_treatment === 'standard' ? 'Standard 20%' : 'Zero-rated'}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {l.target_inactive ? (
                <Badge variant="secondary" className="bg-red-100 text-red-800" data-testid={`extra-line-inactive-${l.id}`}><ShieldAlert className="h-3 w-3 mr-1" />Target inactive</Badge>
              ) : l.needs_check_in_qbo ? (
                <Badge variant="secondary" className="bg-sky-100 text-sky-800" data-testid={`extra-line-unverified-${l.id}`}>{l.qbo_item_name} — Not verified</Badge>
              ) : (
                <Badge className="bg-emerald-600" data-testid={`extra-line-verified-${l.id}`}><ShieldCheck className="h-3 w-3 mr-1" />{l.qbo_item_name}</Badge>
              )}
              {!locked && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(l)} data-testid={`extra-line-edit-btn-${l.id}`}>Edit</Button>
                  <button type="button" onClick={() => remove(l.id)} data-testid={`extra-line-remove-btn-${l.id}`} className="text-slate-400 hover:text-red-600">
                    <X className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {!locked && !adding && (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} disabled={!listsLoaded} data-testid="extra-lines-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" />Add an exceptional service
        </Button>
      )}

      {!locked && adding && (
        <div className="space-y-2 border border-dashed border-sky-300 rounded-md p-3 bg-white" data-testid="extra-line-form">
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })} disabled={!!editingId}>
              <SelectTrigger data-testid="extra-line-service-select"><SelectValue placeholder="Exceptional service" /></SelectTrigger>
              <SelectContent>
                {(editingId ? services : availableServices).map((s) => (
                  <SelectItem key={s.id} value={String(s.id)} data-testid={`extra-line-service-option-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={form.qbo_item_id} onValueChange={(v) => setForm({ ...form, qbo_item_id: v })}>
              <SelectTrigger data-testid="extra-line-item-select"><SelectValue placeholder="QBO item" /></SelectTrigger>
              <SelectContent>
                {items.map((i) => (
                  <SelectItem key={i.id} value={String(i.id)} data-testid={`extra-line-item-option-${i.id}`}>
                    {i.name}{i.qbo_status !== 'verified' ? ' (not verified)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" min="0.01" step="0.01" placeholder="Price for this invoice" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="extra-line-price-input" />
            <Select value={form.vat_treatment} onValueChange={(v) => setForm({ ...form, vat_treatment: v })}>
              <SelectTrigger data-testid="extra-line-vat-select"><SelectValue placeholder="VAT treatment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard" data-testid="extra-line-vat-standard">Standard rate (20%)</SelectItem>
                <SelectItem value="zero_rated" data-testid="extra-line-vat-zero">Zero-rated (0%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="extra-line-description-input" />
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={saving} data-testid="extra-line-save-btn">
              <PlusCircle className="h-3.5 w-3.5 mr-1" />{editingId ? 'Save changes' : 'Add to this invoice'}
            </Button>
            <Button size="sm" variant="ghost" onClick={resetForm} data-testid="extra-line-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500">Never written to this job's own pricing or the site's Customer Master — a one-off addition to this invoice only, which stays off every future invoice unless added again.</p>

      {extraLines.length > 0 && (
        <div className="flex justify-between text-sm pt-1 border-t border-sky-200">
          <span className="text-slate-500">{extraLines.length} extra line(s)</span>
          <span data-testid="extra-lines-total">£{extraLines.reduce((sum, l) => sum + Number(l.unit_price), 0).toFixed(2)} net</span>
        </div>
      )}
    </div>
  );
}
